import { FormEvent, useState } from "react";
import { toast } from "sonner";
import { UserPlus, X, Loader2, Save, User, Mail, Phone, Hash } from "lucide-react";

import { api } from "../../../api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { useLanguage } from "../../../contexts/LanguageContext";
import { clientsLabels } from "../labels";

interface NewClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function NewClientModal({ isOpen, onClose, onSuccess }: NewClientModalProps) {
  const { lang } = useLanguage();
  const t = clientsLabels[lang].form;
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [cpf, setCpf] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    setError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t.requiredName);
      return;
    }

    setSaving(true);
    try {
      const response = await api.createClient({
        name: trimmedName,
        email: email.trim() || null,
        phone: phone.trim() || null,
        cpf: cpf.trim() || null,
      });
      if (!response.ok) {
        setError(response.error || t.genericError);
        return;
      }
      toast.success(t.success);
      onSuccess();
      onClose();
      // Reset form
      setName(""); setEmail(""); setPhone(""); setCpf("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-neutral-900/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div 
        className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 py-6 border-b border-neutral-50 bg-neutral-50/30 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600">
              <UserPlus className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-neutral-800 tracking-tight">{t.title}</h2>
              <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">{lang === 'pt' ? 'Cadastrar novo paciente' : 'Register new patient'}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            className="rounded-xl hover:bg-neutral-100 transition-all active:scale-90"
          >
            <X className="w-5 h-5 text-neutral-400" />
          </Button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-8 space-y-6">
            <div className="space-y-1">
              <Label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1 mb-1.5 block">
                {t.name}
              </Label>
              <div className="relative">
                <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-neutral-400">
                  <User className="w-4 h-4" />
                </div>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={lang === 'pt' ? 'Nome completo' : 'Full name'}
                  className="pl-11 h-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 font-medium"
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1 mb-1.5 block">
                  {t.cpf}
                </Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-neutral-400">
                    <Hash className="w-4 h-4" />
                  </div>
                  <Input
                    value={cpf}
                    onChange={(e) => setCpf(e.target.value)}
                    placeholder="000.000.000-00"
                    className="pl-11 h-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 font-medium"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1 mb-1.5 block">
                  {t.phone}
                </Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-neutral-400">
                    <Phone className="w-4 h-4" />
                  </div>
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(00) 00000-0000"
                    className="pl-11 h-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 font-medium"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1 mb-1.5 block">
                {t.email}
              </Label>
              <div className="relative">
                <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-neutral-400">
                  <Mail className="w-4 h-4" />
                </div>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="exemplo@email.com"
                  className="pl-11 h-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 font-medium"
                />
              </div>
            </div>

            {error && (
              <div className="p-4 rounded-2xl bg-red-50 border border-red-100 text-red-600 text-xs font-bold uppercase tracking-wider text-center">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-8 py-6 border-t border-neutral-50 bg-neutral-50/30 flex items-center justify-end gap-4">
            <Button 
              type="button" 
              variant="ghost" 
              onClick={onClose}
              className="h-12 px-8 font-bold text-neutral-400 rounded-2xl hover:bg-neutral-100 transition-all active:scale-95"
            >
              {t.cancel}
            </Button>
            <Button 
              type="submit" 
              disabled={saving}
              className="h-12 px-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl transition-all active:scale-[0.98] shadow-none flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? t.saving : t.submit}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
